/**
 * WMN multi-signal fuzzy search scorer (dependency-free).
 * Normalize → field weights → exact/prefix/substring/Levenshtein/Dice → sort.
 */
(function (window) {
  'use strict';

  var FIELD_WEIGHTS = {
    title: 1,
    artist: 0.85,
    handle: 0.7,
    tags: 0.45
  };

  var EXACT_TOKEN = 120;
  var PREFIX_TOKEN = 90;
  var SUBSTRING = 55;
  var LEV_BASE = 70;
  var DICE_WEIGHT = 40;
  var COVERAGE_BONUS = 80;
  var AFFINITY_SCALE = 0.08;
  var ORDER_PRIOR = 0.02;

  function normalize(str) {
    var s = String(str || '').toLowerCase();
    try {
      s = s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    } catch (e) {
      /* older browsers */
    }
    return s
      .replace(/['']/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/^\s+|\s+$/g, '')
      .replace(/\s+/g, ' ');
  }

  function tokens(str) {
    var n = normalize(str);
    if (!n) return [];
    return n.split(' ');
  }

  function levenshtein(a, b) {
    a = String(a || '');
    b = String(b || '');
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    if (a.length > b.length) {
      var tmp = a;
      a = b;
      b = tmp;
    }
    var prev = new Array(a.length + 1);
    var i;
    var j;
    for (i = 0; i <= a.length; i++) prev[i] = i;
    for (j = 1; j <= b.length; j++) {
      var cur = [j];
      for (i = 1; i <= a.length; i++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        cur[i] = Math.min(prev[i] + 1, cur[i - 1] + 1, prev[i - 1] + cost);
      }
      prev = cur;
    }
    return prev[a.length];
  }

  function maxLevAllowed(len) {
    if (len <= 3) return 0;
    if (len <= 5) return 1;
    return 2;
  }

  function levOk(dist, aLen, bLen) {
    var m = Math.max(aLen, bLen);
    if (!m) return false;
    if (dist / m > 0.34) return false;
    return dist <= maxLevAllowed(Math.min(aLen, bLen));
  }

  function bigrams(str) {
    var s = normalize(str).replace(/\s+/g, '');
    var out = [];
    if (s.length < 2) return out;
    for (var i = 0; i < s.length - 1; i++) {
      out.push(s.charAt(i) + s.charAt(i + 1));
    }
    return out;
  }

  function diceCoefficient(a, b) {
    var ba = bigrams(a);
    var bb = bigrams(b);
    if (!ba.length || !bb.length) return 0;
    var counts = {};
    var i;
    for (i = 0; i < ba.length; i++) {
      counts[ba[i]] = (counts[ba[i]] || 0) + 1;
    }
    var intersect = 0;
    for (i = 0; i < bb.length; i++) {
      var g = bb[i];
      if (counts[g]) {
        intersect++;
        counts[g]--;
      }
    }
    return (2 * intersect) / (ba.length + bb.length);
  }

  function bestTokenScore(qToken, fieldTokens, fieldNorm) {
    var best = 0;
    var qi;
    if (!qToken) return 0;
    if (fieldNorm === qToken) best = Math.max(best, EXACT_TOKEN * 1.2);
    if (fieldNorm.indexOf(qToken) !== -1) {
      best = Math.max(best, SUBSTRING);
    }
    for (qi = 0; qi < fieldTokens.length; qi++) {
      var ft = fieldTokens[qi];
      if (!ft) continue;
      if (ft === qToken) {
        best = Math.max(best, EXACT_TOKEN);
        continue;
      }
      // Prefix: "john" must not match "johnson" / "johnny" / "johnnie".
      if (ft.indexOf(qToken) === 0) {
        var extra = ft.length - qToken.length;
        if (qToken.length <= 4 && extra > 1) {
          /* weak short prefix — ignore */
        } else {
          best = Math.max(best, PREFIX_TOKEN);
        }
        continue;
      }
      if (qToken.indexOf(ft) === 0 && ft.length >= 3) {
        var qExtra = qToken.length - ft.length;
        if (ft.length <= 4 && qExtra > 1) {
          /* ignore */
        } else {
          best = Math.max(best, PREFIX_TOKEN * 0.75);
        }
      }
      // Field token is a substring of the query ("in" inside "tinariwen") —
      // only allow for substantial tokens or we false-positive half the catalog.
      if (ft.indexOf(qToken) !== -1) {
        best = Math.max(best, SUBSTRING * 0.85);
      } else if (ft.length >= 4 && qToken.indexOf(ft) !== -1) {
        best = Math.max(best, SUBSTRING * 0.85);
      }
      var dist = levenshtein(qToken, ft);
      if (levOk(dist, qToken.length, ft.length)) {
        var levScore =
          LEV_BASE * (1 - dist / Math.max(qToken.length, ft.length, 1));
        best = Math.max(best, levScore);
      }
    }
    return best;
  }

  function fieldBundle(item) {
    var title = item.title || '';
    var artist = item.artist || item.type || '';
    var handle = String(item.handle || '')
      .replace(/-/g, ' ');
    var tags = '';
    if (Array.isArray(item.genres) && item.genres.length) {
      // Mirror genre hard-filter (fy-focus-hard-v7): sole genre tag OR
      // title/handle word-bound evidence. Drops Highlife (jazz+highlife)
      // and Music of Africa (blues+jazz+folk) from contributing "jazz".
      var genres = item.genres;
      var titleLow = String(title).toLowerCase();
      var handleLow = String(item.handle || '').toLowerCase();
      var kept = [];
      var gi;
      for (gi = 0; gi < genres.length; gi++) {
        var gSlug = String(genres[gi] || '');
        if (!gSlug) continue;
        var gWords = gSlug.replace(/-/g, ' ').trim();
        if (!gWords) continue;
        if (genres.length === 1) {
          kept.push(gWords);
          continue;
        }
        if (handleLow.indexOf(gSlug) !== -1) {
          kept.push(gWords);
          continue;
        }
        var gRe = new RegExp(
          '\\b' + gWords.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b',
          'i'
        );
        if (gRe.test(titleLow) || gRe.test(handleLow.replace(/-/g, ' '))) {
          kept.push(gWords);
        }
      }
      tags = kept.join(' ');
    } else if (Array.isArray(item.tags)) tags = item.tags.join(' ');
    else if (typeof item.tags === 'string') tags = item.tags;
    return {
      title: title,
      artist: artist,
      handle: handle,
      tags: tags
    };
  }

  function scoreItem(query, item, opts) {
    opts = opts || {};
    var qTokens = tokens(query);
    if (!qTokens.length) return 0;

    var fields = fieldBundle(item);
    var matchedTokens = 0;
    var fuzzy = 0;
    var f;
    var t;

    for (t = 0; t < qTokens.length; t++) {
      var qt = qTokens[t];
      var tokenBest = 0;
      for (f in FIELD_WEIGHTS) {
        if (!Object.prototype.hasOwnProperty.call(FIELD_WEIGHTS, f)) continue;
        var raw = fields[f] || '';
        var fn = normalize(raw);
        var ftoks = tokens(raw);
        var s = bestTokenScore(qt, ftoks, fn) * FIELD_WEIGHTS[f];
        if (s > tokenBest) tokenBest = s;
      }
      // Distinctive tokens (coltrane, tinariwen) must hit — short ones
      // alone ("john") created Johnson / Johnny Cash false positives.
      if (qt.length >= 5 && tokenBest <= 0) {
        return 0;
      }
      if (tokenBest > 0) matchedTokens++;
      fuzzy += tokenBest;
    }

    var coverage = matchedTokens / qTokens.length;
    fuzzy += coverage * COVERAGE_BONUS;

    var titleDice = diceCoefficient(query, fields.title);
    if (titleDice >= 0.35) {
      fuzzy += titleDice * DICE_WEIGHT;
    }

    var affinityBoost = 0;
    if (
      opts.affinity &&
      item.handle &&
      opts.affinity.handles &&
      opts.affinity.handles[item.handle]
    ) {
      affinityBoost = opts.affinity.handles[item.handle] * AFFINITY_SCALE;
    }

    var base = fuzzy + affinityBoost;
    // Order prior only breaks ties among real matches — never creates a match.
    if (base <= 0) return 0;

    var orderPrior = 0;
    if (typeof opts.index === 'number') {
      orderPrior = Math.max(0, 50 - opts.index) * ORDER_PRIOR;
    }

    return base + orderPrior;
  }

  /**
   * Rank candidates by fuzzy score. Returns a new array (top `limit`).
   * Items with score <= 0 are dropped when `requireMatch` is true (default for non-empty query).
   */
  function rank(query, items, options) {
    options = options || {};
    var limit = options.limit || 32;
    var requireMatch = options.requireMatch !== false;
    var affinity = options.affinity || null;
    var list = Array.isArray(items) ? items : [];
    var q = normalize(query);
    if (!q) {
      return list.slice(0, limit);
    }

    var scored = [];
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (!item) continue;
      var s = scoreItem(query, item, { affinity: affinity, index: i });
      // Require a real lexical hit — ignore near-zero noise from order prior.
      if (requireMatch && s < 40) continue;
      scored.push({ item: item, score: s });
    }
    scored.sort(function (a, b) {
      return b.score - a.score;
    });
    var out = [];
    for (var j = 0; j < scored.length && out.length < limit; j++) {
      out.push(scored[j].item);
    }
    return out;
  }

  window.wmnSearchFuzzy = {
    normalize: normalize,
    tokens: tokens,
    levenshtein: levenshtein,
    scoreItem: scoreItem,
    rank: rank
  };
})(window);
